import axios from 'axios';
import { Action, createAction, handleActions } from 'redux-actions';
import { FULFILLED, PENDING, REJECTED } from './action-type.util';
import { IChangeStatusBody, IStore as IModel, IUploadManyMenusBody } from './store.model';
import {
    getAxiosData,
    getRejectedRespData,
    initPagingList,
    IPaging,
    parsePagingToParams,
    parseToPaging,
    SERVER_API_URL,
} from '../config/constants';
import { parseEmptyField } from '../shared/utils/utils';

export const ACTION_TYPES = {
    UPDATE_STATE: 'store/UPDATE_STATE',
    SET_PAGING: 'store/SET_PAGING',
    RESET_PAGING: 'store/RESET_PAGING',
    GET: 'store/GET',
    GET_LIST: 'store/GET_LIST',
    GET_BY_ID: 'store/GET_BY_ID',
    DELETE: 'store/DELETE',
    SAVE: 'store/SAVE',
    CHANGE_MANY_STATUSES: 'store/CHANGE_MANY_STATUSES',
    UPLOAD_MANY_MENUS: 'store/UPLOAD_MANY_MENUS',
};

export interface IStoreState {
    loading: boolean;
    error: any;
    list: IModel[];
    listShort: IModel[];
    data: IModel;
    paging: IPaging;
    successUpdateStores: string[];
    failedUpdateStores: string[];
}

const initState = {
    loading: false,
    error: null,
    list: [],
    listShort: [],
    data: {} as IModel,
    paging: { ...initPagingList },
    successUpdateStores: [],
    failedUpdateStores: [],
};

export const updateState = createAction(ACTION_TYPES.UPDATE_STATE);
export const setPaging = createAction(ACTION_TYPES.SET_PAGING);
export const resetPaging = createAction(ACTION_TYPES.RESET_PAGING);

export const get = createAction<any, any>(ACTION_TYPES.GET, (params?: any) =>
    getAxiosData(
        axios.get(`${SERVER_API_URL}/stores`, {
            params: parsePagingToParams({ ...initPagingList, ...params }),
        }),
    ),
);
export const getList = createAction(ACTION_TYPES.GET_LIST, () =>
    getAxiosData(axios.get(`${SERVER_API_URL}/stores/list`)),
);
export const getById = createAction(ACTION_TYPES.GET_BY_ID, (id: number) =>
    getAxiosData(axios.get(`${SERVER_API_URL}/stores/${id}`)),
);
export const remove = createAction(ACTION_TYPES.DELETE, async (id: number) => {
    await getAxiosData(axios.delete(`${SERVER_API_URL}/stores/${id}`));
    return id;
});
export const save = createAction(ACTION_TYPES.SAVE, (body: IModel) =>
    body.id
        ? getAxiosData(axios.put(`${SERVER_API_URL}/stores/${body.id}`, parseEmptyField(body)))
        : getAxiosData(axios.post(`${SERVER_API_URL}/stores`, parseEmptyField(body))),
);

export const changeManyStatuses = createAction(ACTION_TYPES.CHANGE_MANY_STATUSES, (body: IChangeStatusBody) =>
    getAxiosData(axios.put(`${SERVER_API_URL}/stores/statuses`, parseEmptyField(body))),
);

export const uploadManyMenus = createAction(ACTION_TYPES.UPLOAD_MANY_MENUS, (body: IUploadManyMenusBody) =>
    getAxiosData(axios.post(`${SERVER_API_URL}/stores/upload-menus`, parseEmptyField(body))),
);

/* Reducer function */
const onPending = (state: IStoreState): IStoreState => ({ ...state, loading: true });
// const onPendingCancel = (state: IStoreState): IStoreState => ({ ...state, loading: false });
const onRejected = (state: IStoreState, action: Action<any>): IStoreState => ({
    ...state,
    error: getRejectedRespData(action.payload),
    loading: false,
});
const onGetListShort = (state: IStoreState, action: Action<any[]>): IStoreState => ({
    ...state,
    loading: false,
    listShort: [...action.payload],
});
const onSetItem = (state: IStoreState, action: Action<any>): IStoreState => ({
    ...state,
    loading: false,
    data: { ...action.payload },
});
/* Reducer function end */

export default handleActions<IStoreState, any>(
    {
        [ACTION_TYPES.UPDATE_STATE]: (state: IStoreState, action: Action<any>): IStoreState => ({
            ...state,
            ...action.payload,
        }),
        [ACTION_TYPES.SET_PAGING]: (state: IStoreState, action: Action<IPaging>): IStoreState => ({
            ...state,
            paging: { ...state.paging, ...parseToPaging(action.payload) },
        }),
        [ACTION_TYPES.RESET_PAGING]: (state: IStoreState): IStoreState => ({
            ...state,
            paging: { ...initPagingList },
        }),

        [PENDING(ACTION_TYPES.GET)]: onPending,
        [REJECTED(ACTION_TYPES.GET)]: onRejected,
        [FULFILLED(ACTION_TYPES.GET)]: (state: IStoreState, action: Action<any>): IStoreState => {
            const { page: list = [], ...paging } = action.payload;
            return { ...state, loading: false, list, paging: { ...state.paging, ...paging } };
        },

        [PENDING(ACTION_TYPES.GET_BY_ID)]: onPending,
        [REJECTED(ACTION_TYPES.GET_BY_ID)]: onRejected,
        [FULFILLED(ACTION_TYPES.GET_BY_ID)]: onSetItem,

        [PENDING(ACTION_TYPES.GET_LIST)]: onPending,
        [REJECTED(ACTION_TYPES.GET_LIST)]: onRejected,
        [FULFILLED(ACTION_TYPES.GET_LIST)]: onGetListShort,

        [PENDING(ACTION_TYPES.DELETE)]: onPending,
        [REJECTED(ACTION_TYPES.DELETE)]: onRejected,
        [FULFILLED(ACTION_TYPES.DELETE)]: (state: IStoreState, action: Action<any>): IStoreState => ({
            ...state,
            loading: false,
            list: state.list.filter((el: IModel) => el.id !== action.payload),
            listShort: state.listShort.filter((el: IModel) => el.id !== action.payload),
        }),

        [PENDING(ACTION_TYPES.SAVE)]: onPending,
        [REJECTED(ACTION_TYPES.SAVE)]: onRejected,
        [FULFILLED(ACTION_TYPES.SAVE)]: onSetItem,

        [PENDING(ACTION_TYPES.CHANGE_MANY_STATUSES)]: onPending,
        [REJECTED(ACTION_TYPES.CHANGE_MANY_STATUSES)]: onRejected,
        [FULFILLED(ACTION_TYPES.CHANGE_MANY_STATUSES)]: (state: IStoreState, action: Action<any>): IStoreState => {
            const { success: successUpdateStores = [], failed: failedUpdateStores = [] } = action.payload;
            return { ...state, loading: false, successUpdateStores, failedUpdateStores };
        },

        [PENDING(ACTION_TYPES.UPLOAD_MANY_MENUS)]: onPending,
        [REJECTED(ACTION_TYPES.UPLOAD_MANY_MENUS)]: onRejected,
        [FULFILLED(ACTION_TYPES.UPLOAD_MANY_MENUS)]: onSetItem,

        /* WS events */
        /* WS events end */
    },
    initState,
);
